import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Download, ChevronDown, ChevronRight, Euro, TrendingUp, FileText, User, AlertCircle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const ReportMensile = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/monthly-sub-agents`, { params: { year, month } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento report');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const exportPdf = async () => {
    if (!data) return;
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/monthly-sub-agents/pdf`, {
        params: { year, month },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-provvigioni-${year}-${String(month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella generazione del PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const exportCsv = () => {
    if (!data?.sub_agenti) return;
    const rows = [];
    rows.push(['Sub-Agente', 'Email', 'Codice Pratica', 'Codice Gestore', 'Cliente', 'CF', 'POD', 'PDR', 'Tipologia', 'Tipo Operazione', 'Fornitore', 'Offerta', 'Stato', 'Data Inserimento', 'Metodo Pagamento', 'Tipo Bolletta', 'Importo da pagare', 'Incassato Compagnia', 'Pagata']);
    data.sub_agenti.forEach(agent => {
      agent.contratti.forEach(c => {
        rows.push([
          agent.sub_agente_nome,
          agent.email || '',
          c.codice_pratica_interno || '',
          c.codice_risposta_gestore || '',
          c.cliente_nome || '',
          c.codice_fiscale || '',
          c.pod || '',
          c.pdr || '',
          c.tipologia || '',
          c.tipo_operazione || '',
          c.fornitore || '',
          c.offerta || '',
          c.stato || '',
          c.data_inserimento || '',
          c.metodo_pagamento || '',
          c.tipo_bolletta || '',
          c.importo_da_pagare,
          c.incassato_da_compagnia ? 'SI' : 'NO',
          c.commissione_pagata ? 'SI' : 'NO',
        ]);
      });
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Report_SubAgenti_${year}_${String(month).padStart(2, '0')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="report-mensile-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Report Mensile Sub-Agenti</h1>
          <p className="text-slate-500 mt-1 text-sm">Contratti e importi da pagare per ogni sub-agente</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Mese</label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="mese-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESI.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Anno</label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-24" data-testid="anno-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" /><span className="hidden sm:inline">Esporta </span>CSV
          </Button>
          <Button
            size="sm"
            onClick={exportPdf}
            disabled={!data || exportingPdf}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-1.5" />
            {exportingPdf ? 'Preparo...' : <>PDF<span className="hidden sm:inline"> (commercialista)</span></>}
          </Button>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          {/* Totali */}
          {data?.totali && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Da pagare ai sub</p>
                <p className="text-2xl font-bold text-emerald-600 tabular-nums">{data.totali.totale_da_pagare.toFixed(2)}€</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Incassato da compagnie</p>
                <p className="text-2xl font-bold text-sky-600 tabular-nums">{data.totali.totale_incassato.toFixed(2)}€</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Margine agenzia</p>
                <p className="text-2xl font-bold text-violet-600 tabular-nums">{data.totali.totale_margine.toFixed(2)}€</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Contratti / Agenti</p>
                <p className="text-2xl font-bold text-slate-700 tabular-nums">{data.totali.n_contratti} / {data.totali.n_sub_agenti}</p>
              </CardContent></Card>
            </div>
          )}

          {/* Lista sub-agenti */}
          <div className="space-y-3">
            {(data?.sub_agenti || []).length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-500">Nessun contratto nel periodo selezionato</CardContent></Card>
            ) : (
              data.sub_agenti.map(agent => {
                const isOpen = expanded[agent.sub_agente_id];
                return (
                  <Card key={agent.sub_agente_id} data-testid={`agent-${agent.sub_agente_id}`}>
                    <CardContent className="p-0">
                      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50" onClick={() => toggleExpand(agent.sub_agente_id)}>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <User className="w-5 h-5 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900">{agent.sub_agente_nome}</p>
                          <p className="text-xs text-slate-500 truncate">{agent.email}</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-slate-500"><FileText className="w-3 h-3" /> {agent.n_contratti}</span>
                          <span className="flex items-center gap-1 text-emerald-600"><TrendingUp className="w-3 h-3" /> {agent.n_attivi}</span>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <Euro className="w-3 h-3 mr-1" /> {agent.totale_da_pagare.toFixed(2)}€
                          </Badge>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="border-t border-slate-100">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="text-left px-4 py-2 text-xs font-medium text-slate-500">Codice</th>
                                  <th className="text-left px-4 py-2 text-xs font-medium text-slate-500">Cliente</th>
                                  <th className="text-left px-4 py-2 text-xs font-medium text-slate-500">Fornitore / Offerta</th>
                                  <th className="text-center px-4 py-2 text-xs font-medium text-slate-500">Tipologia</th>
                                  <th className="text-center px-4 py-2 text-xs font-medium text-slate-500">Stato</th>
                                  <th className="text-center px-4 py-2 text-xs font-medium text-slate-500">Metodo Pagamento</th>
                                  <th className="text-right px-4 py-2 text-xs font-medium text-slate-500">Importo</th>
                                  <th className="text-center px-4 py-2 text-xs font-medium text-slate-500">Inc.Comp.</th>
                                  <th className="text-center px-4 py-2 text-xs font-medium text-slate-500">Pagato</th>
                                </tr>
                              </thead>
                              <tbody>
                                {agent.contratti.map(c => (
                                  <tr key={c.contract_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                                    <td className="px-4 py-2 font-mono text-[11px] text-sky-700">
                                      {c.codice_pratica_interno || '-'}
                                    </td>
                                    <td className="px-4 py-2">
                                      <p className="font-medium text-slate-900 truncate">{c.cliente_nome}</p>
                                      {c.codice_fiscale && <p className="text-[10px] text-slate-400 font-mono">{c.codice_fiscale}</p>}
                                      {(c.pod || c.pdr) && <p className="text-[10px] text-slate-500 font-mono">{c.pod || c.pdr}</p>}
                                    </td>
                                    <td className="px-4 py-2">
                                      <p className="text-xs text-slate-700">{c.fornitore || '-'}</p>
                                      <p className="text-[10px] text-slate-400">{c.offerta || '-'}</p>
                                    </td>
                                    <td className="px-4 py-2 text-center text-xs capitalize">
                                      {c.tipologia}
                                      {c.tipo_operazione && <p className="text-[10px] text-slate-400">{c.tipo_operazione.replace('_', ' ')}</p>}
                                    </td>
                                    <td className="px-4 py-2 text-center"><Badge variant="outline" className="text-[10px] capitalize">{c.stato}</Badge></td>
                                    <td className="px-4 py-2 text-center text-[10px]">
                                      {(() => {
                                        // Determine primary payment badge from either metodo_pagamento or tipo_bolletta
                                        const metodo = (c.metodo_pagamento || '').toLowerCase();
                                        const tipoBoll = (c.tipo_bolletta || '').toLowerCase();
                                        const badges = [];
                                        if (metodo === 'rid') badges.push({ label: 'RID', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' });
                                        if (metodo === 'bollettino') badges.push({ label: 'Bollettino', cls: 'bg-amber-50 text-amber-700 border-amber-200' });
                                        if (metodo === 'bolletta_web' || tipoBoll === 'web') badges.push({ label: 'Bolletta Web', cls: 'bg-sky-50 text-sky-700 border-sky-200' });
                                        if (metodo === 'bolletta_cartacea' || tipoBoll === 'cartacea') badges.push({ label: 'Cartacea', cls: 'bg-slate-50 text-slate-700 border-slate-200' });
                                        if (badges.length === 0) return <span className="text-slate-300">-</span>;
                                        return badges.map((b, i) => (
                                          <Badge key={i} variant="outline" className={`text-[10px] ${b.cls} ${i > 0 ? 'ml-1' : ''}`}>
                                            {b.label}
                                          </Badge>
                                        ));
                                      })()}
                                    </td>
                                    <td className="px-4 py-2 text-right font-semibold tabular-nums text-emerald-700">{c.importo_da_pagare.toFixed(2)}€</td>
                                    <td className="px-4 py-2 text-center">
                                      {c.incassato_da_compagnia ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <AlertCircle className="w-4 h-4 text-amber-400 mx-auto" />}
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      {c.commissione_pagata ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <AlertCircle className="w-4 h-4 text-slate-300 mx-auto" />}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportMensile;
